import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import FontAwesomeIcon from '@fortawesome/react-fontawesome';
import { faTrashAlt, faPrint, faSpinner,  faFileImage, faImages, faDownload } from '@fortawesome/fontawesome-free-solid';
import { ImageItem } from './';
import { EventPageTemplate } from '../';
import { ImagesActions, FramesActions, PrintActions, MessagesActions, CoverActions } from '../../../actions';
import { LocalStorage, QueryString, LazyLoad, ImagesCache, InfinityScroll } from '../../../helper';
import { Checkbox } from '../../Form';
import { ValidateDate } from '../../../helper';

import './eventImagesPage.scss';

const {REACT_APP_LOCAL_STORAGE_PRINT_ACTIONS_ITEM_NAME} = process.env;
const INFINITY_SCROLL_JUMP = 50;

class EventImagesPage extends Component {
    constructor(props) {
        super(props);

        this.state = {
            filter: null,
            filterButtonActive: {
                all: true,
                printed: false,
                notPrinted: false,
                trash: false
            },
            filterd: {
                printed: [],
                notPrinted: [],
                trash: [],
                all: []
            },
            counter: {
                all: 0,
                printed: 0,
                notPrinted: 0,
                trash: 0
            },
            printFullPageOnly: true,
            infinityScrollCurrentLenght: INFINITY_SCROLL_JUMP,
            streamError: false
        }
        
        this.stream = null;
        this.lazyLoad = new LazyLoad();

        try {
            this.cache = new ImagesCache();
            this.cache.open();
        } catch (e) {
            this.cache = false;
        }

        this.updateAllImagesWhenPrintSuccess = this.updateAllImagesWhenPrintSuccess.bind(this);
    }

    componentDidMount() {
        const {events, location} = this.props;
        if(events.currentEventID) {
            this.getAllImages();
            this.createImagesStream();
        }

        this.parseUrlQuery(location.search);
        LocalStorage.onChange(this.updateAllImagesWhenPrintSuccess);

        this.infinityScroll = new InfinityScroll(() => {
            this.setState({
                infinityScrollCurrentLenght: this.state.infinityScrollCurrentLenght + INFINITY_SCROLL_JUMP
            });
        });
    }

    componentWillUnmount() {
        LocalStorage.removeOnChange(this.updateAllImagesWhenPrintSuccess);
        this.lazyLoad.clear(); // remove all images from lazyload

        if(this.infinityScroll) this.infinityScroll.remove();
        if(this.stream) this.stream.close();
    }

    /**
     * for direct access to this page
     * before currentEventID exists so when componentDidMount invoke currentEventID missing
     */
    componentDidUpdate(prevProps) {
        const {events, location, images} = this.props;

        if(prevProps.events.currentEventID !== events.currentEventID) {
            this.getAllImages();
        }
        
        // validate its called only once per images change 
        // becuase inside 'filterImagesArray' has setState
        if((images && images.list && images.list[events.currentEventID]) !== this.state.filterd.all && prevProps.images !== images) {
            this.filterImagesArray();
        }

        this.createImagesStream();
        
        if(prevProps.location.search !== location.search) {
            this.parseUrlQuery(location.search);
        }
    }

    createImagesStream() {
        const {events, dispatch} = this.props;

        // validate stream not exists before & has event data
        if(!events.list || !events.list[events.currentEventID] || this.stream) return;

        // create stream only if event is happening now
        const {start, end, timeZone} = events.list[events.currentEventID].date;
        const eventStarted = ValidateDate.isSameOrBeforeNow(start, timeZone);
        const eventFinised = ValidateDate.itsPast(end, timeZone);

        if(!eventStarted || eventFinised) return;

        this.stream = dispatch(ImagesActions.imageStream(events.currentEventID));

        this.stream.on('error', (e) => {
            if(!this.state.streamError) this.setState({streamError: true});
        });

        // if was connection error
        // in reconnect get all images for insure that no image missing
        this.stream.on('open', (e) => {
            if(this.state.streamError) {
                this.getAllImages();
                this.setState({streamError: false});
            }
        });
    }

    getAllImages() {
        const {dispatch, events} = this.props;

        dispatch(FramesActions.getAllFrames(events.currentEventID));
        dispatch(ImagesActions.getAllImages(events.currentEventID));
        dispatch(CoverActions.getCover(events.currentEventID));
    }

    updateAllImagesWhenPrintSuccess(event) {
        if(event.key !== REACT_APP_LOCAL_STORAGE_PRINT_ACTIONS_ITEM_NAME) return;

        const {dispatch, events} = this.props;
        dispatch(ImagesActions.getAllImages(events.currentEventID));
    }

    parseUrlQuery(string) {
        const {filter} = QueryString.parse(string); // parse location search (query) to object
        
        const filterButtonActive = {
            all: false,
            printed: false,
            notPrinted: false,
            trash: false
        };

        if(filterButtonActive.hasOwnProperty(filter)) {
            filterButtonActive[filter] = true; 
        } else {
            filterButtonActive['all'] = true;
        }

        this.setState({filter: filter || null, filterButtonActive: filterButtonActive});
        this.resetInifintyScroll();
    }

    resetInifintyScroll() {
        if(this.infinityScroll) this.infinityScroll.reset();
        this.setState({infinityScrollCurrentLenght: INFINITY_SCROLL_JUMP});
    }

    filterImagesArray() {
        const {events, images} = this.props;
        const eventID = events.currentEventID;

        const allImages = images && images.list && images.list[eventID];
        if(!allImages) return;

        const addToCounter = {
            all: 0,
            printed: 0,
            notPrinted: 0,
            trash: 0
        }

        const addToFilterd = {
            printed: [],
            notPrinted: [],
            trash: [],
            all: []
        }

        allImages.forEach((item) => {
            let {printed, copies} = item;

            if(printed > 0) {
                addToFilterd.printed.push(item);
                addToCounter.printed += printed;
            }

            if(copies - printed > 0 && copies !== 0) {
                addToFilterd.notPrinted.push(item);
                addToCounter.notPrinted += copies - printed;
            }

            if(copies === 0) addToFilterd.trash.push(item);
            
            addToCounter.all += item.copies;
        });

        // trash count only images (not copies)
        addToCounter.trash = addToFilterd.trash.length;
        addToFilterd.all = allImages;

        this.setState({counter: addToCounter, filterd: addToFilterd});
    }
    
    imageListAfterFilter() {
        const {filterd, filter} = this.state;
        return filterd[filter] || filterd['all'];
    }
    
    handlePrintFullPageOnlyCheckbox() {
        this.setState({printFullPageOnly: !this.state.printFullPageOnly})
    }

    printAll() {
        const {dispatch, frames, events} = this.props;
        const eventID = events.currentEventID || false;

        let frame = frames && frames.list && frames.list[eventID] && frames.list[eventID][0];
        
        if(!frame) {
            return dispatch(MessagesActions.pushError('לא הוגדרו מסגרת וגודל דף'))
        }

        const imagesAfterFilter = this.state.filterd.all.filter(item => item.copies !== 0);

        const printSettings = {
            images: imagesAfterFilter,
            frame: frame,
            printed: false
        }

        if(this.state.printFullPageOnly) {
            dispatch(PrintActions.createFullPagePrintList(printSettings));
        } else {
            dispatch(PrintActions.createPagePrintList(printSettings));
        }
    }    

    addToLazyLoad(element) {
        if(!element) return;

        const img = element && element.current;
        if(!img) return;
        this.lazyLoad.push(img);       
    }

    download() {
        const {events, images, dispatch} = this.props;

        
        const {list, currentEventID} = events;
        const eventData = currentEventID && list && list[currentEventID];
        const imagesData = this.state.filterd.all;
        const downloadData = images.download || {};

        if(downloadData.process && downloadData.eventID !== currentEventID) {
            return dispatch(MessagesActions.pushError('אנא המתינו לסיום הורדת התמונות של אירוע אחר'))
        }
        
        if(downloadData.process) return;

        // filter out trash images
        const imagesWithoutTrash = imagesData.filter((item) => item.copies !== 0);

        dispatch(ImagesActions.downloadAllImages(eventData, imagesWithoutTrash));
    }

    render() {
        const {printed, notPrinted, all, trash} = this.state.filterButtonActive;
        const {counter} = this.state;
        const {images, events} = this.props;
        const downloadData = images.download || {}; 
        const downloadProcess = downloadData.process;

        // prevent loaded all the images at once
        // use infinity scroll to load images part by part
        const items = this.imageListAfterFilter()
            .slice(0, this.state.infinityScrollCurrentLenght)
            .map((item) => {
                return <ImageItem cache={this.cache} onImageReady={this.addToLazyLoad.bind(this)} data={item} key={item._id}/>
            });

        return (
            <EventPageTemplate id="eventImagesPage" level2Tabs={
                <div className="second-nav-container">
                    <div>
                        <Link className={`${all && 'active'}`} to={'./images'}>
                            <FontAwesomeIcon icon={faImages}/> הכל
                            <div className="budget">{counter.all}</div>
                        </Link>
                        <Link className={`${notPrinted && 'active'}`} to={'./images?filter=notPrinted'}>
                            <FontAwesomeIcon icon={faFileImage}/> לא הודפסו
                            <div className="budget">{counter.notPrinted}</div>
                        </Link>
                        <Link className={`${printed && 'active'}`} to={'./images?filter=printed'}>
                            <FontAwesomeIcon icon={faPrint}/> הודפסו
                            <div className="budget">{counter.printed}</div>
                        </Link>
                        <Link className={`${trash && 'active'}`} to={'./images?filter=trash'}>
                            <FontAwesomeIcon icon={faTrashAlt}/> פח
                            <div className="budget">{counter.trash}</div>
                        </Link>
                    </div>
                    <div className="print-buttons">
                        <div className={`download-btn ${downloadProcess ? ' disabled': ''} ${downloadData.error ? ' error' : ''}`} onClick={this.download.bind(this)}>
                            {!downloadProcess ? <FontAwesomeIcon icon={faDownload}/> : <FontAwesomeIcon spin icon={faSpinner}/>}
                            {!downloadProcess ? 
                                " הורדה": 
                                downloadData.eventID === events.currentEventID ? 
                                    ` ${downloadData.downloadedChunks}/${downloadData.chunks} ${downloadData.error ? 'שגיאה בהורדה!' : 'מוריד...'}` : 
                                    " המתינו להורדת אירוע אחר"
                            }
                        </div>
                        <div className="print-btn" onClick={this.printAll.bind(this)}>
                            <FontAwesomeIcon icon={faPrint}/> הדפסה
                        </div>
                        <div className="print-full-page-checkbox">
                            <Checkbox
                                name="closed"
                                isChecked={this.state.printFullPageOnly}
                                disabled={false}
                                onChange={this.handlePrintFullPageOnlyCheckbox.bind(this)}
                                label="הדפסת דפים מלאים בלבד"
                                labelSide="left"
                            />
                        </div>
                    </div>
                </div>
            }>
                <div id="imagesList">
                    {items}
                </div>
                <div className={`placeholder ${items.length !== 0 ? 'hide' : ''}`}>
                    <div className="icon">
                        <FontAwesomeIcon icon={faImages}/>
                    </div>
                    לא נמצאו תמונות
                </div>
            </EventPageTemplate>
        )
    }
}

const mapStateToProps = (state) => {
    return {
        images: state.imagesReducer,
        events: state.eventsReducer,
        frames: state.framesReducer
    }
}

const EventImagesPageWithRedux = withRouter(connect(mapStateToProps)(EventImagesPage));

export {EventImagesPageWithRedux as EventImagesPage};


// WEBPACK FOOTER //
// ./src/components/EventPage/images/EventImagesPage.jsx